/**
 * The catalog, as the browser receives it.
 *
 * These types describe the JSON that `Catalog.describe(subject)` returns —
 * they add no vocabulary of their own. Anywhere the server sends a list of
 * legal values (operators, measures, visualizations, units, actors) the type
 * is `string` and the list is the payload: the builder renders whatever the
 * catalog offers and would render a sixteenth property, a fourth event or a
 * seventh measure the day one is authored, without a line changing here.
 */

export interface Operator {
  name: string
  label: string
  /** How many inputs the operator needs: none | one | two | many. */
  arity: string
}

export interface Coverage {
  hit_rate: number
  measured_on?: string | null
}

export interface Range {
  min: number
  max: number
  /**
   * Why these bounds are what they are. For a spatial axis it says NOMINAL:
   * the range is a drawing fallback, not the pitch any match was played on.
   */
  basis?: string
}

export interface Property {
  name: string
  label: string
  description?: string
  type: string
  unit?: string | null
  unit_label?: string
  phrase?: string
  /** Which input to render: select | multi_select | number | toggle | text. */
  control?: string
  required?: boolean
  operators: Operator[]
  has_values: boolean
  values?: unknown[]
  spatial_axis?: string
  /** `length` or `width` — which pitch dimension this axis measures. */
  pitch_dimension?: string
  range?: Range
  coverage?: Coverage
  identity_caveat?: string
}

export interface MeasureFn {
  name: string
  label: string
  description?: string
  applies_to: string[]
  /** none | property | condition — what the measure needs as its operand. */
  operand: string
  result_unit?: string
  /** Whether adding a column of this measure up produces a real number. */
  additive?: boolean
}

export interface EventProperty {
  name: string
  label: string
  type: string
  unit?: string | null
  description?: string
  operators: Operator[]
}

export interface CatalogEvent {
  name: string
  label: string
  description?: string
  /** The event as a noun in a sentence: "a progressive pass". */
  phrase?: string
  properties: EventProperty[]
}

export interface Named {
  name: string
  label: string
  description?: string
  default?: boolean
}

export interface TimeUnit extends Named {
  kind: string
  aliases?: string[]
}

export interface Visualization {
  name: string
  label: string
  description?: string
  /** Tokens the result must satisfy before this drawing is offered. */
  requires: string[]
  /**
   * Whether the drawing renders one measure, the one `visualize.value` names.
   * A table shows every measure it was given; everything else has to pick.
   */
  single_measure?: boolean
}

export interface Warning {
  name: string
  severity: string
  summary: string
  detail?: string
  verified?: string
}

export interface SubjectInfo {
  name: string
  label: string
  plural: string
  description?: string
  grain?: string
  default_measures?: string[]
}

export interface SubjectCatalog {
  catalog_version: string
  subject: SubjectInfo
  properties: Property[]
  measures: MeasureFn[]
  events: CatalogEvent[]
  sequence_actors: Named[]
  time_units: TimeUnit[]
  visualizations: Visualization[]
  warnings: Warning[]
}

export interface RootCatalog {
  catalog_version: string
  subjects: SubjectInfo[]
  visualizations: Visualization[]
  warnings: Warning[]
}

// ----------------------------------------------------------------- lookups

export const propertyBy = (cat: SubjectCatalog, name: string): Property | undefined =>
  cat.properties.find((p) => p.name === name)

export const eventBy = (cat: SubjectCatalog, name: string): CatalogEvent | undefined =>
  cat.events.find((e) => e.name === name)

export const measureBy = (cat: SubjectCatalog, fn: string): MeasureFn | undefined =>
  cat.measures.find((m) => m.name === fn)

export const operatorBy = (prop: Property, op: string): Operator | undefined =>
  prop.operators.find((o) => o.name === op)

/** Properties the analyst must pin before anything can run (the match). */
export const required = (cat: SubjectCatalog): Property[] =>
  cat.properties.filter((p) => p.required)

export const optional = (cat: SubjectCatalog): Property[] =>
  cat.properties.filter((p) => !p.required)

/** The property carrying a given pitch axis, if the subject has one. */
export const axisProperty = (cat: SubjectCatalog, axis: string): Property | undefined =>
  cat.properties.find((p) => p.spatial_axis === axis)

// ----------------------------------------------------- visualization gating

/**
 * Why a visualization cannot be used for the analysis as authored.
 *
 * The catalog says what each drawing `requires`; this is the only place the
 * browser interprets those tokens, and an unrecognised one *blocks* rather
 * than passes. Failing open would mean a new requirement authored server-side
 * is silently ignored by every old browser tab — the drawing would be offered
 * for a result that cannot support it.
 */
export function unmet(
  viz: Visualization,
  cat: SubjectCatalog,
  breakdownProperties: string[],
): string[] {
  const reasons: string[] = []
  for (const token of viz.requires ?? []) {
    if (token === 'breakdown') {
      if (breakdownProperties.length === 0) reasons.push('needs a breakdown')
    } else if (token === 'spatial_breakdowns') {
      const axes = new Set(
        breakdownProperties
          .map((n) => propertyBy(cat, n)?.spatial_axis)
          .filter(Boolean) as string[],
      )
      if (!(axes.has('x') && axes.has('y'))) {
        reasons.push('needs a breakdown on both pitch axes')
      }
    } else if (token === 'two_breakdowns') {
      // Exactly two: one axis each. Three breakdowns have no side left to sit
      // on, and one leaves a grid a single column wide, which is the bar chart
      // with extra lines around it.
      if (breakdownProperties.length !== 2) {
        reasons.push('needs exactly two breakdowns')
      }
    } else {
      reasons.push(`needs "${token}", which this build does not know how to check`)
    }
  }
  return reasons
}
